import * as Sentry from '@sentry/react';
import { tenantType, userType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import { analyticsClient } from './analyticsClient';
import { ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS } from './analyticsEvents';

interface ErrorAnalyticsAttributes {
	errorType?: string;
	userAgent?: string;
	extensionVersion?: string;
	httpStatusCode?: number;
	requestMethod?: string;
	requestPath?: string;
	environment?: string;
	siteId?: string;
	userId?: string;
}

/**
 * Extracts relevant error information from Sentry event for analytics
 */
const extractErrorAttributes = (event: Sentry.ErrorEvent): ErrorAnalyticsAttributes => {
	const attributes: ErrorAnalyticsAttributes = {
		environment: event.environment,
		userAgent: event.request?.headers?.['User-Agent'],
		extensionVersion: event.tags?.rovoExtensionVersion as string,
		siteId: event.tags?.siteId as string,
		userId: event.user?.id?.toString(),
	};

	// Extract error type
	if (event.exception?.values?.[0]) {
		const exception = event.exception.values[0];
		attributes.errorType = exception.type;
	}

	if (event.contexts?.response) {
		attributes.httpStatusCode = event.contexts.response.status_code as number;
	}

	if (event.request?.method) {
		attributes.requestMethod = event.request.method;

		try {
			const url = event.request.url ? new URL(event.request.url).pathname : undefined;
			attributes.requestPath = url;
		} catch {
			// Invalid URL, skip
		}
	}

	return attributes;
};

/**
 * Sends error analytics event based on Sentry error event.
 * Only sends analytics for error and fatal level events to avoid inflating
 * error counts with info and warning level logs.
 */
export const sendErrorAnalytics = (event: Sentry.ErrorEvent): void => {
	if (event.level !== 'error' && event.level !== 'fatal') {
		return;
	}

	const attributes = extractErrorAttributes(event);

	// Set tenant info if available
	if (attributes.siteId) {
		analyticsClient.setTenantInfo(tenantType.CLOUD_ID, attributes.siteId);
	}

	// Set user info if available
	if (attributes.userId) {
		analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, attributes.userId);
	}

	// https://data-portal.internal.atlassian.com/analytics/registry/86838
	analyticsClient
		.sendTrackEvent({
			source: 'rovoExtensionFrontend',
			action: 'error',
			actionSubject: ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS.ROVO_EXTENSION_FRONTEND,
			attributes: {
				...attributes,
			},
		})
		.catch(() => {
			// do nothing
		});
};
